import { Check } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { Icon } from 'components/icons/Icon';

export interface DailyCheckInCompletionSummary {
  goalsUpdated: number;
  goalsSkipped: number;
  pulseOutcome: 'saved' | 'skipped' | 'disabled' | 'omitted';
  targetDate: string;
  pulseOnly?: boolean;
  segmentOutcomes?: Array<'saved' | 'skipped'>;
}

interface DailyCheckInCompletionProps extends DailyCheckInCompletionSummary {
  actionLabel?: string;
  onAction?: () => void;
}

const plural = (value: number, word: string) => `${value} ${word}${value === 1 ? '' : 's'}`;

const formatDate = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date(year, month - 1, day));
};

export const buildDailyCheckInCompletionCopy = (summary: DailyCheckInCompletionSummary) => {
  const allSkipped = summary.goalsUpdated === 0
    && summary.goalsSkipped > 0
    && summary.pulseOutcome !== 'saved';
  const pulseOnly = Boolean(summary.pulseOnly)
    || (summary.goalsUpdated === 0 && summary.goalsSkipped === 0);
  const pieces = [
    summary.goalsUpdated ? `${plural(summary.goalsUpdated, 'goal')} updated` : '',
    summary.goalsSkipped ? `${plural(summary.goalsSkipped, 'goal')} skipped` : '',
    summary.pulseOutcome === 'saved' ? 'Daily Pulse saved' : '',
    summary.pulseOutcome === 'skipped' ? 'Daily Pulse skipped for today' : '',
    summary.pulseOutcome === 'disabled' ? 'Daily Pulse is turned off' : '',
  ].filter(Boolean);

  if (pulseOnly && summary.pulseOutcome === 'saved') {
    return {
      title: 'Daily Pulse saved',
      summary: formatDate(summary.targetDate),
      toast: `Daily Pulse saved for ${formatDate(summary.targetDate)}`,
      allSkipped: false,
      sunrise: false,
    };
  }
  if (allSkipped || (pulseOnly && summary.pulseOutcome === 'skipped')) {
    const joined = pieces.join(' · ');
    return {
      title: 'Check-in closed',
      summary: joined,
      toast: joined ? `Check-in closed · ${joined}` : 'Check-in closed',
      allSkipped: true,
      sunrise: false,
    };
  }
  const joined = pieces.join(' · ');
  return {
    title: 'Daily Check-in complete',
    summary: joined,
    toast: `Daily Check-in complete${joined ? ` · ${joined}` : ''}`,
    allSkipped: false,
    sunrise: summary.goalsUpdated > 0,
  };
};

export const DailyCheckInCompletion = ({ actionLabel, onAction, ...summary }: DailyCheckInCompletionProps) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const copy = buildDailyCheckInCompletionCopy(summary);
  const segmentOutcomes = summary.segmentOutcomes || [
    ...Array.from({ length: summary.goalsUpdated }, () => 'saved' as const),
    ...Array.from({ length: summary.goalsSkipped }, () => 'skipped' as const),
    ...(summary.pulseOutcome === 'saved' || summary.pulseOutcome === 'skipped'
      ? [summary.pulseOutcome]
      : []),
  ];
  const resolvedSegments = segmentOutcomes.length ? segmentOutcomes : ['saved' as const];

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div
      className={`daily-checkin-completion ${copy.sunrise ? 'daily-checkin-completion--sunrise' : 'daily-checkin-completion--quiet'}`}
      role="status"
      aria-live="polite"
      data-testid="daily-checkin-completion"
    >
      <div className="daily-checkin-completion__segments" aria-hidden="true">
        {resolvedSegments.map((outcome, index) => (
          <span
            key={index}
            className={`daily-checkin-completion__segment--${outcome}`}
            data-testid="goal-celebration-segment"
            style={{ animationDelay: `${index * 160}ms` }}
          />
        ))}
      </div>
      {copy.sunrise ? (
        <div
          className="daily-checkin-completion__landscape"
          aria-hidden="true"
          data-testid="daily-checkin-completion-sunrise"
        >
          <span className="daily-checkin-completion__sun" />
          <div className="daily-checkin-completion__hill daily-checkin-completion__hill--back">
            <svg viewBox="0 0 520 150" preserveAspectRatio="none">
              <path className="daily-checkin-completion__ridge daily-checkin-completion__ridge--back" data-testid="daily-checkin-completion-ridge" d="M0 117 C78 84 118 102 176 72 C238 40 273 91 333 66 C398 38 438 67 520 34 L520 150 L0 150 Z" />
            </svg>
          </div>
          <div className="daily-checkin-completion__hill daily-checkin-completion__hill--front">
            <svg viewBox="0 0 520 150" preserveAspectRatio="none">
              <path className="daily-checkin-completion__ridge daily-checkin-completion__ridge--front" data-testid="daily-checkin-completion-ridge" d="M0 132 C69 103 121 119 180 94 C244 66 302 116 358 88 C417 58 469 92 520 74 L520 150 L0 150 Z" />
            </svg>
          </div>
          {Array.from({ length: 3 }, (_, index) => (
            <span key={index} className={`daily-checkin-completion__spark daily-checkin-completion__spark--${index + 1}`} />
          ))}
        </div>
      ) : null}
      {copy.sunrise ? (
        <div className="daily-checkin-completion__mark" aria-hidden="true" data-testid="goal-celebration-success-mark">
          <Check size={27} strokeWidth={2.8} />
        </div>
      ) : copy.allSkipped ? (
        <div className="daily-checkin-completion__neutral-mark" aria-hidden="true" data-testid="goal-celebration-neutral-mark">–</div>
      ) : (
        <Icon
          name="orangeCheckmark"
          size={58}
          className="daily-checkin-completion__saved-mark"
          aria-hidden="true"
          data-testid="goal-celebration-success-mark"
        />
      )}
      <div className="daily-checkin-completion__copy">
        <span className="daily-checkin-completion__eyebrow">Daily check-in</span>
        <h2 ref={headingRef} className="daily-checkin-completion__title" tabIndex={-1}>{copy.title}</h2>
        <p className="daily-checkin-completion__headline">{copy.summary}</p>
        {actionLabel && onAction ? (
          <button type="button" className="daily-checkin-completion__action" onClick={onAction}>
            {actionLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
};
